import { REGION } from '@env';
import { Subject } from 'rxjs';

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ElementRef,
  Component,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Optional,
  Self,
  ViewChild,
  OnChanges,
  OnInit,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { NgForm, NgControl, FormGroupDirective, ControlValueAccessor } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';

import { GooglePlaceResult } from '@lib/geo/google/places';

class PlacesAutocompleteControlBase {
  constructor(
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public ngControl: NgControl,
  ) {}
}

@Component({
  selector: 'sn-place-autocomplete-control',
  templateUrl: './places-autocomplete.control.html',
  styleUrls: ['./places-autocomplete.control.scss'],
  inputs: ['disabled'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SnPlacesAutocompleteControl,
    },
  ],
})
export class SnPlacesAutocompleteControl
  extends PlacesAutocompleteControlBase
  implements OnChanges, OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string>
{
  public static nextId = 0;

  public stateChanges: Subject<void> = new Subject();

  public disabled: boolean;

  public errorState: boolean;

  @Input()
  get value(): string {
    return this._value;
  }
  set value(value: string) {
    if (value !== this.value) {
      this._value = value;
      this.stateChanges.next();
      this._controlValueAccessorChangeFn(value);
      this.ngControl.control.markAsTouched();
    }
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req: boolean) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  @Input()
  public options: google.maps.places.AutocompleteOptions;

  @Input()
  public autocomplete: boolean = true;

  @Output()
  public readonly placeChange = new EventEmitter<GooglePlaceResult>();

  @HostBinding()
  public id = `sn-password-control-${SnPlacesAutocompleteControl.nextId++}`;

  @HostBinding('attr.aria-describedby')
  public describedBy = '';

  @ViewChild('input', { static: true })
  public inputRef: ElementRef;

  public focused = false;
  public hidden = true;

  public controlType = 'sn-password-control';

  private _value: string;
  private _placeholder: string;
  private _required = false;

  private _autocomplete: google.maps.places.Autocomplete;
  private _eventListener: google.maps.MapsEventListener;

  constructor(
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective,
    @Optional() @Self() public ngControl: NgControl,
    defaultErrorStateMatcher: ErrorStateMatcher,
  ) {
    super(parentForm, parentFormGroup, defaultErrorStateMatcher, ngControl);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get empty(): boolean {
    return !this.value;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this.stateChanges.next();
    }
  }

  public ngOnInit(): void {
    this._init();
    this.stateChanges.next();
  }

  public ngOnDestroy(): void {
    if (this._eventListener) {
      this._eventListener.remove();
    }
    this.stateChanges.complete();
  }

  @HostListener('focus')
  public focusHandler(): void {
    this.onContainerClick(null);
  }

  public setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  public onContainerClick(event: MouseEvent): void {
    this.inputRef.nativeElement.focus();
  }

  public writeValue(value: any): void {
    this.value = value;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._controlValueAccessorChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._controlValueAccessorTouchedFn = fn;
  }

  private _controlValueAccessorChangeFn: (value: any) => void = () => {};
  private _controlValueAccessorTouchedFn: () => void = () => {};

  private _init(): void {
    if (!this.autocomplete) {
      return;
    }
    this._autocomplete = new google.maps.places.Autocomplete(
      this.inputRef.nativeElement,
      this.options,
    );

    if (!this._autocomplete) {
      throw new Error('Autocomplete is not initialized');
    }

    if (!this._autocomplete.addListener !== null) {
      this._eventListener = this._autocomplete.addListener('place_changed', () => {
        const autocompletePlace = this._autocomplete.getPlace();
        const place = new GooglePlaceResult(autocompletePlace);

        if (REGION.toString() === 'UK') {
          this.writeValue(place.formattedAddress);
        } else {
          this.writeValue(place.addressComponents.address);
        }
        this.placeChange.emit(place);
      });
    }
  }
}
